import { Device, getDevice } from 'utils/getDevice';
import { createContext, Dispatch, SetStateAction, useContext } from 'react';
import { BillboardsConfig } from 'types/configs/billboard-config';
import { AdsConfig } from 'types/configs/ads-config';
import { AppConfig } from 'types/configs/app-config';
import { CaptionsUpdate } from 'components/VideoPlayer/hooks/useCaptions';
import { DEFAULT_CAPTIONS_DATA } from './useContextSetup';

export const AppContext = createContext<AppContextType>({
  device: getDevice(),
  appConfigUrl: undefined,
  appConfig: undefined,
  adsConfig: undefined,
  billboardsConfig: undefined,
  captionsData: DEFAULT_CAPTIONS_DATA,
  webosDeviceInfo: undefined,
  currentPollingEnabled: true,
  boxscorePollingEnabled: true,
  appControlsPollingEnabled: true,
  currentFetchCounter: 0,
  boxscoreFetchCounter: 0,
  appControlsFetchCounter: 0,
  incrementCurrentFetchCounter: () => undefined,
  incrementBoxscoreFetchCounter: () => undefined,
  incrementAppControlsFetchCounter: () => undefined,
  setCurrentPollingEnabled: () => undefined,
  setBoxscorePollingEnabled: () => undefined,
  setAppControlsPollingEnabled: () => undefined,
  setAppConfigUrl: () => undefined,
  setAppConfig: () => undefined,
  setAdsConfig: () => undefined,
  setBillboardsConfig: () => undefined,
  setWebOSDeviceInfo: () => undefined,
});
AppContext.displayName = 'AppContext';

export type AppContextType = {
  adsConfig: AdsConfig | undefined;
  appConfig: AppConfig | undefined;
  appConfigUrl: string | undefined;
  appControlsFetchCounter: number;
  appControlsPollingEnabled: boolean;
  billboardsConfig: BillboardsConfig | undefined;
  boxscoreFetchCounter: number;
  boxscorePollingEnabled: boolean;
  currentFetchCounter: number;
  currentPollingEnabled: boolean;
  device: Device;
  incrementAppControlsFetchCounter: () => void;
  incrementBoxscoreFetchCounter: () => void;
  incrementCurrentFetchCounter: () => void;
  setAdsConfig: Dispatch<SetStateAction<AdsConfig | undefined>>;
  setAppConfig: Dispatch<SetStateAction<AppConfig | undefined>>;
  setAppConfigUrl: Dispatch<SetStateAction<string | undefined>>;
  setAppControlsPollingEnabled: Dispatch<SetStateAction<boolean>>;
  setBillboardsConfig: Dispatch<SetStateAction<BillboardsConfig | undefined>>;
  setBoxscorePollingEnabled: Dispatch<SetStateAction<boolean>>;
  setCurrentPollingEnabled: Dispatch<SetStateAction<boolean>>;
  setWebOSDeviceInfo: Dispatch<SetStateAction<DeviceInfo | undefined>>;
  webosDeviceInfo: DeviceInfo | undefined;
  captionsData: CaptionsUpdate;
};

export const useAppContext = (): AppContextType => useContext(AppContext);
